'use client';
/**
 * ⑭ R14perf — 전역 검색의 «가벼운 자리». GlobalSearch(제안 엔진·시트·포커스 가두기 포함)를 첫 수화 경로에서 뺀다.
 *
 *   · 서버 HTML·첫 수화 = GlobalSearch 닫힌 상태와 같은 마크업(데스크톱 입력창 + 모바일 아이콘 44 · CSS 로만 가름).
 *   · 첫 상호작용(입력창 포커스 · 아이콘 누름)에서 GlobalSearch 를 불러와 그 자리에 넘겨준다(autoOpen · 친 글자 인계).
 *     모듈은 유휴 시간에 미리 받아 둔다(nav-slot-bodies) — 누른 뒤 기다림 없이 열린다.
 *   · 기능은 옮겼을 뿐 뺀 것 없음: 시트·포커스 가두기·Esc·safe-area·계측(search_open 등)은 전부 GlobalSearch 그대로.
 *
 * search_unify1 A — 검색창 «하나»(kd-gs): 헤더·홈 히어로(lg)·/search·지역 허브가 이 마크업 한 벌을 쓴다.
 *   JS 없이도 동작: <form action="/search" method="get"> + name="q" + hidden src(header·hero·search·hub — 로깅용).
 *   「검색」 버튼은 모든 자리에 있다(lg 만 보이고 md 는 시각 숨김) — 자리마다 DOM 이 같다(DOM 동일 테스트).
 *
 * ⛔ 방문자 분기 없음(로그인·봇·UA). 훅은 없다 — 넘겨받기 판단은 호출부(SearchSlotBody · KdSearchBox)가 한다.
 */
import React from 'react'; // vitest(클래식 JSX) 렌더 검증용
import './global-nav.css';
import './kd-gs.css';

export const SEARCH_PLACEHOLDER = '단지·지역·구역 검색';

/** 로깅용 자리 값(search_logs.source). */
export type SearchSrc = 'header' | 'hero' | 'search' | 'hub';
export type SearchSize = 'md' | 'lg';

export const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

/** 루트 클래스 — kd-gs kd-gs--{variant} [kd-gs--lg] [호출부 클래스]. */
export function kdGsClass(variant: 'desktop' | 'mobile', size: SearchSize = 'md', className?: string): string {
  return `kd-gs kd-gs--${variant}${size === 'lg' ? ' kd-gs--lg' : ''}${className ? ` ${className}` : ''}`;
}

/** 입력창 뒤 꼬리(숨은 src + 「검색」 버튼) — 셸·GlobalSearch 공용(한 벌). */
export function KdGsTail({ src }: { src: SearchSrc }) {
  return (
    <>
      <input type="hidden" name="src" value={src} />
      <button type="submit" className="kd-gs__go">검색</button>
    </>
  );
}

export type SearchArm = (variant: 'desktop' | 'mobile', input: HTMLInputElement | null) => void;

/** GlobalSearch variant="desktop" 닫힌 상태와 같은 마크업. */
export function SearchShellDesktop({ onArm, className, src = 'header', size = 'md', defaultQ = '' }: { onArm: SearchArm; className?: string; src?: SearchSrc; size?: SearchSize; defaultQ?: string }) {
  return (
    <div className={kdGsClass('desktop', size, className)}>
      <form role="search" className="kd-gs__field" action="/search" method="get">
        <SearchIcon />
        <input
          type="search"
          name="q"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={false}
          aria-label="통합 검색"
          defaultValue={defaultQ}
          placeholder={SEARCH_PLACEHOLDER}
          autoComplete="off"
          enterKeyHint="search"
          onFocus={(e) => onArm('desktop', e.currentTarget)}
        />
        <KdGsTail src={src} />
      </form>
    </div>
  );
}

/** GlobalSearch variant="mobile" 닫힌 상태와 같은 마크업(아이콘 44 → 시트). */
export function SearchShellMobile({ onArm, className }: { onArm: SearchArm; className?: string }) {
  return (
    <div className={kdGsClass('mobile', 'md', className)}>
      <button type="button" className="kd-gs__icon" aria-label="검색 열기" aria-haspopup="dialog" aria-expanded={false} onClick={() => onArm('mobile', null)}>
        <SearchIcon />
      </button>
    </div>
  );
}
